import React, { useState } from 'react';
import {
  Layers, Sparkles, CheckCircle, Mountain, Target,
  Zap, Brain, ArrowRight, ChevronDown, ChevronRight,
  Search, Filter, Download, Copy, Eye, Star
} from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './priorities-ai-design/ProductDesignDocPrimitives';

export const IconLibrary: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const iconCategories = {
    all: 'All Icons',
    architecture: 'Architecture',
    actions: 'Actions',
    navigation: 'Navigation',
    status: 'Status'
  };

  const icons = [
    // Architecture Icons
    { name: 'Layers', component: Layers, category: 'architecture', description: 'M3 Encoding, Framework layers' },
    { name: 'Mountain', component: Mountain, category: 'architecture', description: 'Peak Priorities brand, elevation' },
    { name: 'Target', component: Target, category: 'architecture', description: 'Focus, precision, outcomes' },
    { name: 'Brain', component: Brain, category: 'architecture', description: 'Intelligence, decision-making' },

    // Status Icons
    { name: 'Sparkles', component: Sparkles, category: 'status', description: 'Featured, highlighted content' },
    { name: 'CheckCircle', component: CheckCircle, category: 'status', description: 'Validated, complete' },
    { name: 'Star', component: Star, category: 'status', description: 'Priority, excellence' },

    // Actions
    { name: 'Zap', component: Zap, category: 'actions', description: 'Quick action, energy' },
    { name: 'Download', component: Download, category: 'actions', description: 'Export, save' },
    { name: 'Copy', component: Copy, category: 'actions', description: 'Duplicate, clone' },
    { name: 'Eye', component: Eye, category: 'actions', description: 'View, preview' },

    // Navigation
    { name: 'ArrowRight', component: ArrowRight, category: 'navigation', description: 'Next, continue' },
    { name: 'ChevronDown', component: ChevronDown, category: 'navigation', description: 'Expand, show more' },
    { name: 'ChevronRight', component: ChevronRight, category: 'navigation', description: 'Navigate, drill down' },
    { name: 'Search', component: Search, category: 'navigation', description: 'Find, discover' },
    { name: 'Filter', component: Filter, category: 'navigation', description: 'Sort, organize' }
  ];

  const filteredIcons = selectedCategory === 'all'
    ? icons
    : icons.filter(icon => icon.category === selectedCategory);

  return (
    <div>
      <ProductDesignPageHeader
        title="Icon Library"
        eyebrow="Iconography · components"
        backTo="/design-language"
        backLabel="Design language"
        subtitle="Comprehensive icon system for Peak Priorities interfaces. Each icon carries semantic meaning aligned with our architecture."
      />

      <ProductDesignSection title="Icon set" hint="Filter by category. Lucide is the default icon family.">
        <div className="mb-6 flex flex-wrap gap-2">
          {Object.entries(iconCategories).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={` px-4 py-2 text-sm font-medium transition-colors ${
                selectedCategory === key
                  ? 'bg-[var(--slate)] text-white'
                  : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-6 md:grid-cols-4 lg:grid-cols-6">
          {filteredIcons.map(({ name, component: IconComponent, description }) => (
            <div
              key={name}
              className="border border-[var(--rule)] bg-[var(--surface)] p-6 text-center transition-colors hover:bg-[var(--hover-tint)]"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="bg-[var(--surface-raised)] p-3">
                  <IconComponent className="h-8 w-8 text-[var(--text-sec)]" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[var(--text-pri)]">{name}</h3>
                  <p className="mt-1 text-xs text-[var(--text-ter)]">{description}</p>
                </div>
                <code className="rounded bg-[var(--surface-raised)] px-2 py-1 font-mono text-xs text-[var(--text-sec)]">
                  {name}
                </code>
              </div>
            </div>
          ))}
        </div>
      </ProductDesignSection>

      <ProductDesignSection title="Usage Guidelines">
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h3 className="mb-3 font-heading font-semibold text-[var(--text-pri)]">Size Standards</h3>
            <div className="space-y-2 text-sm text-[var(--text-sec)]">
              <div>• <strong className="text-[var(--text-pri)]">Small:</strong> 16px (h-4 w-4) — Inline text, labels</div>
              <div>• <strong className="text-[var(--text-pri)]">Medium:</strong> 20px (h-5 w-5) — Buttons, cards</div>
              <div>• <strong className="text-[var(--text-pri)]">Large:</strong> 24px (h-6 w-6) — Headers, emphasis</div>
              <div>• <strong className="text-[var(--text-pri)]">XLarge:</strong> 32px (h-8 w-8) — Hero sections</div>
            </div>
          </div>

          <div>
            <h3 className="mb-3 font-heading font-semibold text-[var(--text-pri)]">Color Usage</h3>
            <div className="space-y-2 text-sm text-[var(--text-sec)]">
              <div>• <strong className="text-[var(--text-pri)]">Default:</strong> var(--text-sec) — Standard content</div>
              <div>• <strong className="text-[var(--text-pri)]">Primary:</strong> primary-600 (sage) — Active states</div>
              <div>• <strong className="text-[var(--text-pri)]">Success:</strong> primary-600 — Positive actions</div>
              <div>• <strong className="text-[var(--text-pri)]">Warning:</strong> amber-600 — Attention needed</div>
              <div>• <strong className="text-[var(--text-pri)]">Error:</strong> coral-600 — Problems, alerts</div>
            </div>
          </div>
        </div>
      </ProductDesignSection>
    </div>
  );
};
